import { GoogleMap } from '@react-google-maps/api';
import { constants } from '@trucktrax/trucktrax-common';
import {
  OrderDto,
  PlantDto,
  PositionDto,
  ProductLine,
  TicketDto,
  VoidFunc,
} from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { getOrder } from '../../../../services/ordersService';
import { fetchStatusList } from '../../../../services/statusesService';
import { ConnectedDispatchFunction, MapMarkerType } from '../../../../types';
import { getIdFromUrl } from '../../../../util/appUtil';
import { retrieveProductLineNameDiff } from '../../../../util/statusesUtil';
import { effectiveProductLine } from '../../../../util/ticketUtil';
import JobsiteMarker from '../../../geotrax/map/markers/JobsiteMarker';
import PlantMarker from '../../../geotrax/map/markers/PlantMarker';
import PositionMarker from '../../../geotrax/map/markers/PositionMarker';
import routeStyles from './RouteInfoMap.module.css';

interface RouteInfoMapProps {
  mapCentered: boolean;
  plantList: PlantDto[];
  positionsList: {
    data: {
      items: PositionDto[];
    };
  };
  selectedItemId?: string;
  selectedProductLine?: ProductLine;
  /**
   * DEV-3924: ticket-derived product line threaded from RouteInfo (see
   * RouteInfoProps.productLineOverride for the undefined/null/value semantics).
   */
  productLineOverride?: ProductLine | null;
  selectedTicket: TicketDto;
  isTicketPrintViewCenter: boolean;
  getOrder: ConnectedDispatchFunction<typeof getOrder>;
  recenterMap: VoidFunc;
  updateSelectedItem: (position: PositionDto) => void;
  clearSelectedItem: VoidFunc;
}

interface RouteInfoMapState {
  order?: OrderDto;
}

interface MapCenter {
  lat: number;
  lng: number;
}

interface PositionBounds {
  latMin: number;
  latMax: number;
  lngMin: number;
  lngMax: number;
}

export class RouteInfoMap extends Component<RouteInfoMapProps, RouteInfoMapState> {
  static defaultProps: Partial<RouteInfoMapProps> = {
    selectedItemId: undefined,
    selectedProductLine: undefined,
    productLineOverride: undefined,
  };

  private readonly defaultZoom = 10;

  private readonly maximumNumberOfSVG = 500;

  private mapRef?: google.maps.Map;

  constructor(props: RouteInfoMapProps) {
    super(props);
    this.state = {};
  }

  async componentDidMount(): Promise<void> {
    await this.loadOrderData();
  }

  componentDidUpdate(prevProps: RouteInfoMapProps): void {
    this.handleMapCenteringUpdates(prevProps);
    this.handlePrintModeUpdates(prevProps);

    // Re-generate bounds when position data changes
    if (this.props.positionsList !== prevProps.positionsList && this.mapRef) {
      this.fitMapToBounds();
    }

    // Handle selection changes - center on selected marker
    if (this.props.selectedItemId !== prevProps.selectedItemId && this.props.selectedItemId) {
      this.centerOnSelectedMarker();
    }
  }

  private centerOnSelectedMarker = (): void => {
    const { selectedItemId, positionsList } = this.props;
    if (!selectedItemId || !this.mapRef) return;

    const selectedPosition = positionsList.data.items.find(p => p.id.toString() === selectedItemId);

    if (selectedPosition?.location?.latitude && selectedPosition?.location?.longitude) {
      const center = new google.maps.LatLng(
        selectedPosition.location.latitude,
        selectedPosition.location.longitude,
      );

      // Smooth pan to the selected marker
      this.mapRef.panTo(center);

      // Optionally zoom in slightly if current zoom is too far out
      const currentZoom = this.mapRef.getZoom();
      if (currentZoom && currentZoom < 12) {
        this.mapRef.setZoom(12);
      }
    }
  };

  private loadOrderData = async (): Promise<void> => {
    const { selectedTicket } = this.props;
    const orderId = getIdFromUrl(selectedTicket.order?.url);

    if (orderId) {
      const order = await this.props.getOrder(Number(orderId), selectedTicket.region?.url);
      this.setState({ order });
    }
  };

  private handleMapCenteringUpdates = (prevProps: RouteInfoMapProps): void => {
    if (this.props.mapCentered !== prevProps.mapCentered && this.props.mapCentered) {
      this.fitMapToBounds();
    }
  };

  private handlePrintModeUpdates = (prevProps: RouteInfoMapProps): void => {
    const { isTicketPrintViewCenter } = this.props;
    if (isTicketPrintViewCenter && isTicketPrintViewCenter !== prevProps.isTicketPrintViewCenter) {
      this.props.recenterMap();
      this.resetZoomForPrint();
    }
  };

  private resetZoomForPrint = (): void => {
    if (this.mapRef && Number(this.mapRef.getZoom()) > this.defaultZoom) {
      this.mapRef.setZoom(this.defaultZoom);
    }
  };

  private onMapLoad = (map: google.maps.Map): void => {
    this.mapRef = map;

    // Immediately try to fit to bounds if we have position data
    this.fitMapToBounds();
  };

  private fitMapToBounds = (): void => {
    if (!this.mapRef) return;

    const { positionsList } = this.props;
    const positions = positionsList.data.items;

    if (positions.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    let hasValidPositions = false;

    // Add all position markers to bounds
    positions.forEach(position => {
      if (position.location?.latitude && position.location?.longitude) {
        bounds.extend(
          new google.maps.LatLng(position.location.latitude, position.location.longitude),
        );
        hasValidPositions = true;
      }
    });

    if (!hasValidPositions) {
      return;
    }

    // Fit the map to the bounds with padding
    this.mapRef.fitBounds(bounds, 50);

    // Limit maximum zoom
    google.maps.event.addListenerOnce(this.mapRef, 'bounds_changed', () => {
      const zoom = this.mapRef?.getZoom();
      if (zoom && zoom > 15) {
        this.mapRef?.setZoom(15);
      }
    });
  };

  private onPositionMarkerClick = (position: PositionDto) => (): void => {
    const positionIdString = position.id.toString();

    // Toggle selection: if clicking the same marker, clear it; otherwise select new one
    if (this.props.selectedItemId === positionIdString) {
      this.props.clearSelectedItem();
    } else {
      this.props.updateSelectedItem(position);
    }
  };

  private onPositionMarkerTooltipClose = (): void => {
    // Simply clear the selection without affecting map position
    this.props.clearSelectedItem();
  };

  private renderPositionMarkers = (): JSX.Element[] => {
    const { selectedTicket, positionsList, selectedItemId, selectedProductLine } = this.props;
    const positions = positionsList.data.items;

    // DEV-3924: in per-ticket mode (override provided) the pin product line and status
    // names come from the ticket-derived product line; unresolved (null) ⇒ None / no
    // names — never the globally selected product line. Without an override the
    // main-map behavior is unchanged (positions and the status map pass through as-is;
    // PositionMarker defaults a missing markerStatusMap to {}).
    const productLine = effectiveProductLine(this.props.productLineOverride, selectedProductLine);
    const markerStatusMap = retrieveProductLineNameDiff(productLine!);

    return positions.map((position: PositionDto) => {
      const positionIdString = position.id.toString();

      const truckDetails = {
        ...position,
        ...(this.props.productLineOverride !== undefined && {
          productLine: productLine ?? ProductLine.None,
        }),
        trackedObject: {
          ...position.trackedObject,
          truckAlias: selectedTicket.externalInfo?.truckAlias,
        },
      };

      return (
        <PositionMarker
          displayPNG={positions.length > this.maximumNumberOfSVG}
          isRouteInfo
          key={position.id}
          position={truckDetails}
          mapRef={this.mapRef}
          selectedItemId={selectedItemId}
          onClick={this.onPositionMarkerClick(position)}
          onCloseClick={this.onPositionMarkerTooltipClose}
          markerStatusMap={markerStatusMap}
          markerData={{
            markerId: positionIdString,
            type: MapMarkerType.Truck,
            markerData: undefined,
            driverDetails: {
              firstName: selectedTicket.externalInfo?.driverName,
            },
            truckDetails,
            ticket: selectedTicket,
          }}
          hideLabel
        />
      );
    });
  };

  private renderPlantMarker = (): JSX.Element | null => {
    const { plantList, selectedTicket } = this.props;
    const plant = plantList?.find(p => p.url === selectedTicket.plant?.url);

    return plant ? <PlantMarker key={plant.id} plant={plant} mapRef={this.mapRef} /> : null;
  };

  private renderJobsiteMarker = (): JSX.Element => {
    const { order } = this.state;
    const { selectedTicket } = this.props;

    if (order) {
      return <JobsiteMarker mapRef={this.mapRef} order={order} />;
    }

    return (
      <JobsiteMarker
        mapRef={this.mapRef}
        order={RouteInfoMap.generateDummyOrder(selectedTicket) as any}
      />
    );
  };

  private static generateDummyOrder = (ticket: TicketDto): Partial<OrderDto> => ({
    geoZone: ticket.geoZone,
    id: undefined,
    orderNumber: undefined,
    customerName: ticket.order?.customerName,
  });

  private hasMapAvailable = (): boolean => {
    const mapCenter = this.getMapCenter();
    const { order } = this.state;
    const positions = this.props.positionsList.data.items;

    const hasPosition =
      positions.length > 0 && positions.some(p => p.location?.latitude || p.location?.longitude);

    return !!(mapCenter || order?.geoZone?.zone.circle?.center?.latitude || hasPosition);
  };

  private getMapCenter = (): MapCenter | undefined => {
    // Always allow map to center on selected items
    const selectedCenter = this.getSelectedItemCenter();
    if (selectedCenter) return selectedCenter;

    // If no selection, return calculated center from bounds
    return this.calculateCenterFromBounds();
  };

  private getSelectedItemCenter = (): MapCenter | undefined => {
    if (!this.props.selectedItemId) return undefined;

    const selectedPosition = this.props.positionsList.data.items.find(
      p => p.id.toString() === this.props.selectedItemId,
    );

    if (selectedPosition?.location?.latitude && selectedPosition?.location?.longitude) {
      return {
        lat: selectedPosition.location.latitude,
        lng: selectedPosition.location.longitude,
      };
    }

    return undefined;
  };

  private calculateCenterFromBounds = (): MapCenter => {
    const positionBounds: PositionBounds = {
      latMin: 90,
      latMax: -90,
      lngMin: 180,
      lngMax: -180,
    };

    this.props.positionsList.data.items.forEach(position => {
      if (position.location?.latitude && position.location?.longitude) {
        positionBounds.latMin = Math.min(positionBounds.latMin, position.location.latitude);
        positionBounds.latMax = Math.max(positionBounds.latMax, position.location.latitude);
        positionBounds.lngMin = Math.min(positionBounds.lngMin, position.location.longitude);
        positionBounds.lngMax = Math.max(positionBounds.lngMax, position.location.longitude);
      }
    });

    const orderCenter = this.state.order?.geoZone?.zone?.circle?.center;
    if (orderCenter) {
      positionBounds.latMin = Math.min(positionBounds.latMin, orderCenter.latitude ?? 90);
      positionBounds.latMax = Math.max(positionBounds.latMax, orderCenter.latitude ?? -90);
      positionBounds.lngMin = Math.min(positionBounds.lngMin, orderCenter.longitude ?? 180);
      positionBounds.lngMax = Math.max(positionBounds.lngMax, orderCenter.longitude ?? -180);
    }

    const latMid = (positionBounds.latMin + positionBounds.latMax) / 2;
    const lngMid = (positionBounds.lngMin + positionBounds.lngMax) / 2;

    return { lat: latMid, lng: lngMid };
  };

  render(): JSX.Element {
    if (!this.hasMapAvailable()) {
      return (
        <div className={cx(routeStyles.noRouteContainer)}>
          <div className={cx(routeStyles.noRouteMessage)}>
            <i className={cx('icon-directions', routeStyles.noRouteIcon)} />
            <p className={cx(routeStyles.noRouteText)}>Route info unavailable</p>
          </div>
        </div>
      );
    }

    const definedZoom = this.mapRef?.getZoom() ?? this.defaultZoom;
    const containerStyle = { width: '100%', height: '100%' };

    // Only set center if we don't have a map ref yet (initial load)
    // After initial load, let the map maintain its own position unless explicitly centered
    const mapOptions: any = {
      zoom: definedZoom,
      draggable: true,
      streetViewControl: false,
      scaleControl: true,
      mapTypeControl: true,
      mapId: process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID',
      mapTypeControlOptions: {
        mapTypeIds: [google.maps.MapTypeId.ROADMAP, google.maps.MapTypeId.SATELLITE],
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      controlSize: constants.GM_CONTROLSIZE,
      panControl: false,
      zoomControl: true,
      rotateControl: false,
      fullscreenControl: false,
    };

    // Only set center when we have a selected item (not on initial load)
    if (this.mapRef && this.props.selectedItemId) {
      mapOptions.center = this.getMapCenter();
    }

    return (
      <GoogleMap mapContainerStyle={containerStyle} options={mapOptions} onLoad={this.onMapLoad}>
        {this.renderPositionMarkers()}
        {this.renderPlantMarker()}
        {this.renderJobsiteMarker()}
      </GoogleMap>
    );
  }
}

export default connect<any, any, any>(
  (state: any) => ({
    plantList: state.plantList,
    selectedProductLine: state.selectedProductLine,
    selectedTicket: state.selectedTicket,
    ticketTimeline: state.ticketTimeline,
    isTicketPrintViewCenter: state.isTicketPrintViewCenter,
  }),
  {
    fetchStatusList,
    getOrder,
  },
)(RouteInfoMap);
